import { SpeechSegmenter } from '@mio/shared';

import { PcmPlayer, type PlaybackSnapshot } from './pcm.service';
import { ScribeMicrophone, type CaptureDiagnostics } from './scribe.service';
import { ServerAudioPlayback } from './server-audio.service';

import type { MioAgentClient } from '../api/mio-agent.client';
import type { MioLiveContext } from '@mio/shared';

export { takeSpeech } from '@mio/shared';

export type VoiceStatus = 'idle' | 'connecting' | 'listening' | 'speaking' | 'paused';
export interface VoiceDiagnostics extends CaptureDiagnostics {
  lastTranscriptAt: number | null;
}
interface VoiceCallbacks {
  status: (status: VoiceStatus) => void;
  caption: (caption: string) => void;
  turn: (text: string) => void;
  interrupt: (playback: PlaybackSnapshot) => void;
  error: (message: string) => void;
  context: () => MioLiveContext | null;
  diagnostics?: (value: VoiceDiagnostics) => void;
}

interface SpeechJob {
  controller: AbortController;
  response: Promise<Response>;
}

/** Scribe endpoints can occur inside a thought; only visibly unfinished clauses need extra time. */
function commitDelay(text: string) {
  const value = text.trim();
  if (/[?!]["')\]]*$/.test(value)) return 80;
  if (/(?:\.{2,}|…|[,;:—-])["')\]]*$/.test(value)) return 900;
  const words = value.replace(/[.\s"')\]]+$/, '');
  return /\b(?:a|an|the|and|or|but|because|if|when|while|for|of|to|with|about|using|like|such as)$/i.test(
    words,
  )
    ? 900
    : 80;
}

/** Start receiving the next response, with byte-based backpressure rather than buffering all audio. */
function prefetchSpeech(response: Response, signal: AbortSignal) {
  if (!response.body) return response;
  const buffer = new TransformStream<Uint8Array, Uint8Array>(
    undefined,
    new ByteLengthQueuingStrategy({ highWaterMark: 16_384 }),
    new ByteLengthQueuingStrategy({ highWaterMark: 131_072 }),
  );
  void response.body.pipeTo(buffer.writable, { signal }).catch(() => undefined);
  return new Response(buffer.readable, { status: response.status, headers: response.headers });
}

/** Owns live audio independently of the companion panel and individual lesson routes. */
export class VoiceSession {
  private mic: ScribeMicrophone | null = null;
  private player: PcmPlayer | null = null;
  private sessionId: string | null = null;
  private audioChannel: AbortController | null = null;
  private serverPlayback: ServerAudioPlayback | null = null;
  private usesServerAudio = false;
  private audioReady = false;
  private generation = 0;
  private active = false;
  private muted = false;
  private partialText = '';
  private interruptedUtterance = false;
  private captureDiagnostics: VoiceDiagnostics = {
    microphone: '',
    rms: 0,
    chunksSent: 0,
    lastTranscriptAt: null,
  };
  private transcript = '';
  private commitTimer: ReturnType<typeof setTimeout> | undefined;
  private heartbeat: ReturnType<typeof setInterval> | undefined;
  private queue: { text: string; turnId: string }[] = [];
  private jobs: SpeechJob[] = [];
  private playbackEpoch = 0;
  private runningEpoch: number | null = null;
  private bufferTurn: string | null = null;
  private completedTurn: string | null = null;
  private segmenter = new SpeechSegmenter((text) => {
    if (!this.bufferTurn || !this.active) return;
    this.queue.push({ text, turnId: this.bufferTurn });
    this.fillRequests();
    void this.pump();
  });
  private closing: Promise<void> = Promise.resolve();

  constructor(
    private client: MioAgentClient,
    private callbacks: VoiceCallbacks,
  ) {}

  get connected() {
    return this.active && (!this.usesServerAudio || this.audioReady);
  }
  get currentTurn() {
    if (this.serverAudio) return this.serverPlayback?.currentTurn ?? null;
    if (this.bufferTurn === this.completedTurn && !this.queue.length && !this.jobs.length) {
      const playback = this.player?.snapshot();
      if (!playback || playback.playedMs >= playback.generatedMs) return null;
    }
    return this.bufferTurn;
  }
  get currentSessionId() {
    return this.sessionId;
  }
  get serverAudio() {
    return this.usesServerAudio;
  }

  expectTurn(turnId: string) {
    if (!this.active || !this.serverAudio || this.bufferTurn === turnId) return;
    this.bufferTurn = turnId;
    this.serverPlayback?.expectTurn(turnId);
  }

  completeTurn(turnId: string) {
    if (this.bufferTurn === turnId) this.completedTurn = turnId;
  }

  async start(threadId: string, deviceId?: string) {
    this.stop();
    const version = ++this.generation;
    this.callbacks.status('connecting');
    // Created/resumed synchronously with the user's start action to satisfy browser audio policy.
    try {
      const player = new PcmPlayer(
        (speaking) => {
          if (this.active && version === this.generation)
            this.callbacks.status(speaking ? 'speaking' : 'listening');
        },
        () => {
          if (this.active && version === this.generation)
            this.pause('Your browser paused playback. Resume voice when you return.');
        },
      );
      this.player = player;
      const current = () => this.active && version === this.generation;
      const recognized = () => {
        this.captureDiagnostics.lastTranscriptAt = Date.now();
        this.callbacks.diagnostics?.({ ...this.captureDiagnostics });
        const playback = this.player?.snapshot() ?? { playedMs: 0, generatedMs: 0 };
        const hasSpeechOutput =
          playback.generatedMs > playback.playedMs || this.jobs.length > 0 || this.queue.length > 0;
        // A partial heard while Mio is still thinking must not cancel its model/tool work.
        // Committed requests still supersede the old turn through callbacks.turn below.
        if (!this.interruptedUtterance && hasSpeechOutput) {
          this.interruptedUtterance = true;
          this.callbacks.interrupt(playback);
        }
      };
      const mic = new ScribeMicrophone({
        onPartial: (text) => {
          if (!current() || this.muted) return;
          if (text !== this.partialText) {
            if (text.trim()) recognized();
            // Changing partials extend the utterance; repeated identical partials do not.
            // A stalled partial must eventually release already committed speech.
            this.scheduleCommit(Boolean(text.trim()));
          }
          this.partialText = text;
          this.callbacks.caption([this.transcript, text].filter(Boolean).join(' '));
        },
        onCommit: (text) => {
          if (!current() || !text.trim() || this.muted) return;
          recognized();
          this.partialText = '';
          this.transcript = [this.transcript, text.trim()].filter(Boolean).join(' ');
          this.callbacks.caption(this.transcript);
          this.scheduleCommit();
        },
        // Energy is useful for the input meter, but fans/Bluetooth noise are not learner turns.
        onActivity: () => undefined,
        onCapture: (capture) => {
          if (!current()) return;
          this.captureDiagnostics = { ...this.captureDiagnostics, ...capture };
          this.callbacks.diagnostics?.({ ...this.captureDiagnostics });
        },
        onError: (message) => {
          if (current()) this.pause(message);
        },
        onInterrupted: () => {
          if (current()) this.pause('Your browser paused audio. Resume voice when you return.');
        },
      });
      this.mic = mic;
      // Resume both audio contexts during the button gesture, before any network/permission await.
      await Promise.all([player.resume(), mic.prepare()]);
      await this.closing;
      if (version !== this.generation) return;
      const session = await this.client.voiceSession(threadId);
      if (version !== this.generation) {
        await this.client.endVoice(session.sessionId);
        return;
      }
      this.sessionId = session.sessionId;
      this.active = true;
      this.usesServerAudio = session.audioTransport === 'server';
      if (this.usesServerAudio) {
        this.serverPlayback = new ServerAudioPlayback(player, (message) => {
          if (current()) this.pause(message);
        });
        await this.connectAudio(session.sessionId, version);
        if (version !== this.generation) return;
      }
      mic.mute(this.muted);
      await mic.start(session.token, deviceId);
      if (version !== this.generation) return;
      const context = this.callbacks.context();
      if (context) await this.client.voiceHeartbeat(session.sessionId, context);
      if (version !== this.generation) return;
      this.callbacks.status('listening');
      this.heartbeat = setInterval(() => {
        const context = this.callbacks.context();
        if (this.sessionId && context) {
          void this.client.voiceHeartbeat(this.sessionId, context).catch(() => {
            if (current()) this.pause('Voice session expired. Reconnect to continue.');
          });
        }
      }, 30_000);
    } catch (cause) {
      if (version !== this.generation) return;
      this.stop();
      this.callbacks.error(cause instanceof Error ? cause.message : 'Could not start voice.');
    }
  }

  private connectAudio(sessionId: string, version: number) {
    const voiceAudio = this.client.voiceAudio?.bind(this.client);
    if (!voiceAudio) throw new Error('Refresh Mio to connect live voice playback.');
    const channel = new AbortController();
    this.audioChannel = channel;
    return new Promise<void>((resolve, reject) => {
      let initial = true;
      let handshakeTimer: ReturnType<typeof setTimeout> | undefined;
      const current = () => !channel.signal.aborted && version === this.generation;
      const onAbort = () => {
        clearTimeout(handshakeTimer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      channel.signal.addEventListener('abort', onAbort, { once: true });
      const run = async () => {
        let recentRenewals: number[] = [];
        try {
          while (current()) {
            let connected = false;
            handshakeTimer = setTimeout(() => {
              if (!current()) return;
              const error = new Error('Mio could not connect live audio. Reconnect voice.');
              reject(error);
              this.pause(error.message);
            }, 10_000);
            await voiceAudio(sessionId, channel.signal, (event) => {
              if (!current()) return;
              if (event.type === 'audio.connected') {
                connected = true;
                this.audioReady = true;
                clearTimeout(handshakeTimer);
                if (initial) {
                  initial = false;
                  resolve();
                } else {
                  this.mic?.mute(this.muted);
                  this.callbacks.status('listening');
                }
              } else if (connected) this.serverPlayback?.receive(event);
              else throw new Error('Mio audio started before its connection was ready.');
            });
            clearTimeout(handshakeTimer);
            if (!current()) break;
            if (!connected) throw new Error('Mio returned no live audio connection.');
            recentRenewals = recentRenewals.filter((at) => Date.now() - at < 10_000);
            if (recentRenewals.length >= 3)
              throw new Error('Mio live audio keeps disconnecting. Reconnect voice.');
            recentRenewals.push(Date.now());
            this.audioReady = false;
            // Renew cleanly without replaying audio or adding a fixed reconnect delay.
            if (this.bufferTurn) {
              this.callbacks.interrupt(this.player?.snapshot() ?? { playedMs: 0, generatedMs: 0 });
              this.interrupt();
            }
            this.mic?.mute(true);
            this.callbacks.status('connecting');
          }
        } catch (cause) {
          if (!current()) return;
          const message = cause instanceof Error ? cause.message : 'Live audio disconnected.';
          reject(cause instanceof Error ? cause : new Error(message));
          if (!initial) this.pause(message);
        } finally {
          clearTimeout(handshakeTimer);
          channel.signal.removeEventListener('abort', onAbort);
        }
      };
      void run();
    });
  }

  private scheduleCommit(waitingForPartial = false) {
    clearTimeout(this.commitTimer);
    this.commitTimer = setTimeout(
      () => {
        if (!this.active || this.muted) return;
        // After the inactivity watchdog expires, only confirmed speech becomes learner input.
        // An uncommitted fragment (often a filler) is not reliable enough to append to it.
        this.partialText = '';
        const text = this.transcript.trim();
        this.transcript = '';
        this.interruptedUtterance = false;
        this.callbacks.caption('');
        if (text) this.callbacks.turn(text);
      },
      waitingForPartial ? 2500 : commitDelay(this.transcript),
    );
  }

  mute(muted: boolean) {
    this.muted = muted;
    this.mic?.mute(muted);
    if (muted) {
      clearTimeout(this.commitTimer);
      this.transcript = '';
      this.partialText = '';
      this.interruptedUtterance = false;
      this.callbacks.caption('');
    }
  }

  append(turnId: string, text: string, final = false) {
    if (!this.active || !this.sessionId || this.serverAudio) return;
    if (this.bufferTurn !== turnId) {
      if (this.bufferTurn) this.interrupt();
      this.bufferTurn = turnId;
      this.segmenter.reset();
    }
    this.segmenter.append(text, final);
  }

  private fillRequests() {
    if (!this.active || !this.sessionId) return;
    while (this.jobs.length < 2 && this.queue.length) {
      const item = this.queue.shift();
      if (!item) break;
      const controller = new AbortController();
      const response = this.client
        .speech(this.sessionId, item.turnId, item.text, controller.signal)
        .then((result) => prefetchSpeech(result, controller.signal));
      // The next response may fail before the ordered playback loop reaches it.
      void response.catch(() => undefined);
      this.jobs.push({ controller, response });
    }
  }

  private async pump() {
    const epoch = this.playbackEpoch;
    if (this.runningEpoch === epoch) return;
    this.runningEpoch = epoch;
    let pending: SpeechJob | undefined;
    try {
      this.fillRequests();
      while (
        this.active &&
        this.jobs.length &&
        this.sessionId &&
        this.player &&
        epoch === this.playbackEpoch
      ) {
        pending = this.jobs[0];
        if (!pending) break;
        const response = await pending.response;
        if (pending.controller.signal.aborted || epoch !== this.playbackEpoch) break;
        await this.player.stream(response, pending.controller.signal);
        if (epoch !== this.playbackEpoch) break;
        this.jobs.shift();
        this.fillRequests();
      }
    } catch (cause) {
      if (epoch === this.playbackEpoch && !pending?.controller.signal.aborted && this.active)
        this.pause(cause instanceof Error ? cause.message : 'Speech playback disconnected.');
    } finally {
      if (this.runningEpoch === epoch) {
        this.runningEpoch = null;
        if (this.active && (this.queue.length || this.jobs.length)) void this.pump();
      }
    }
  }

  interrupt(): PlaybackSnapshot {
    this.playbackEpoch++;
    this.serverPlayback?.interrupt();
    for (const job of this.jobs) job.controller.abort();
    this.jobs = [];
    this.runningEpoch = null;
    this.queue = [];
    this.segmenter.reset();
    this.bufferTurn = null;
    this.completedTurn = null;
    return this.player?.stop() ?? { playedMs: 0, generatedMs: 0 };
  }

  private pause(message: string) {
    this.stop();
    this.callbacks.status('paused');
    this.callbacks.error(message);
  }

  stop() {
    this.generation++;
    this.active = false;
    this.audioChannel?.abort();
    this.audioChannel = null;
    clearInterval(this.heartbeat);
    clearTimeout(this.commitTimer);
    this.interrupt();
    this.serverPlayback = null;
    this.usesServerAudio = false;
    this.audioReady = false;
    this.mic?.stop();
    this.mic = null;
    this.player?.close();
    this.player = null;
    if (this.sessionId) this.closing = this.client.endVoice(this.sessionId).catch(() => undefined);
    this.sessionId = null;
    this.transcript = '';
    this.partialText = '';
    this.interruptedUtterance = false;
    this.muted = false;
    this.callbacks.caption('');
    this.captureDiagnostics = { microphone: '', rms: 0, chunksSent: 0, lastTranscriptAt: null };
    this.callbacks.diagnostics?.({ ...this.captureDiagnostics });
    this.callbacks.status('idle');
  }
}
